import groupBy from "lodash.groupby";

import { ensureFullUrl } from "@intelligems/ig-plugin/core";
import { RedirectDto, ShopifyTemplateTypeType } from "@intelligems/ig-types";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { getSpecificRedirectsUtil } from "@src/features/experiments/components/redirects/getSpecificRedirects";

import { initialRedirectState } from "../../initials";

export const ensureTemplateSuffix = (
  _draft: RedirectDto[],
  group: number
): RedirectDto[] => {
  const existingTemplateSuffixes: string[] = [];

  _draft.forEach((redirect) => {
    if (
      redirect.group === group &&
      redirect.isTemplateTest &&
      redirect.templateSuffixes
    ) {
      redirect.templateSuffixes.forEach((suffix) => {
        if (!existingTemplateSuffixes.includes(suffix)) {
          existingTemplateSuffixes.push(suffix);
        }
      });
    }
  });
  if (existingTemplateSuffixes.length > 0) {
    return _draft.map((redirect) => {
      return {
        ...redirect,
        templateSuffixes:
          redirect.group === group && redirect.isTemplateTest
            ? existingTemplateSuffixes
            : redirect.templateSuffixes,
      };
    });
  }

  return _draft;
};

export const ensureAllTemplateType = (
  _draft: RedirectDto[],
  controlTemplateType: ShopifyTemplateTypeType,
  group: number
): RedirectDto[] => {
  return _draft.map((redirect) => {
    if (
      redirect.group === group &&
      controlTemplateType !== redirect.templateType &&
      redirect.isTemplateTest
    ) {
      return {
        ...redirect,
        templateType:
          redirect.group === group
            ? controlTemplateType
            : redirect.templateType,
        queryParams: [],
      };
    } else return redirect;
  });
};

export const addGroupRedirect = <T extends RedirectDto[]>(
  draft: RedirectDto[],
  testGroup: VariationFe,
  group: number,
  isThemeTest: boolean,
  isTemplateTest: boolean
): RedirectDto[] => {
  const index = draft.findIndex((redirect) => redirect.group === group);
  if (isThemeTest) {
    draft.push(
      initialRedirectState({ variation: testGroup, group, isThemeTest: true })
    );
  } else if (index !== -1) {
    if (isTemplateTest) {
      draft.push(
        initialRedirectState({
          variation: testGroup,
          group: group,
          isThemeTest: false,
          templateType: draft[index].templateType!,
          isTemplateTest: true,
        })
      );
    } else {
      draft.push(
        initialRedirectState({
          variation: testGroup,
          group,
          isThemeTest: false,
          templateType: null,
          isTemplateTest: false,
          copyRedirect: draft[index],
        })
      );
    }
  }

  return draft;
};

export const deleteRedirectsByIds = (
  draft: RedirectDto[],
  redirectIds: string[]
) => {
  draft = draft.filter((redirect) => !redirectIds.includes(redirect.id));
  return draft;
};

export const orderByTestGroup = (
  variations: VariationFe[],
  groupedRedirects: any[]
) => {
  return groupedRedirects.map((redirectGroup) => {
    return [
      redirectGroup[0],
      redirectGroup[1].sort((a, b) => {
        const variationA = variations.find((tg) => tg.id === a.variationId);
        const variationB = variations.find((tg) => tg.id === b.variationId);
        if (variationA && variationB) {
          if (variationA.order > variationB.order) {
            return 1;
          } else if (variationA.order < variationB.order) {
            return -1;
          } else return 0;
        } else return 0;
      }),
    ];
  });
};

export const ensureTestGroupRedirects = (
  draft: RedirectDto[],
  testGroups: VariationFe[]
) => {
  const removedTgRedirects = draft.filter(
    (_) => !testGroups.map((group) => group.id).includes(_.variationId)
  );
  // remove redirects from deleted test groups
  if (removedTgRedirects.length > 0) {
    draft = deleteRedirectsByIds(
      draft,
      removedTgRedirects.map((redirect) => redirect.id)
    );
  }

  testGroups.forEach((testGroup) => {
    // URL redirects
    if (
      draft.filter(
        (redirect) => !redirect.isThemeTest && !redirect.isTemplateTest
      ).length > 0 &&
      !draft
        .filter((redirect) => !redirect.isThemeTest && !redirect.isTemplateTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      const redirectGroupKeys = Object.keys(
        groupBy(
          draft.filter((_) => !_.isThemeTest && !_.isTemplateTest),
          (_) => _.group
        )
      );

      redirectGroupKeys.forEach((group) => {
        // update redirect "group" w new test group field
        draft = addGroupRedirect(draft, testGroup, Number(group), false, false);
      });
    }

    // theme redirects
    if (
      draft.filter((redirect) => redirect.isThemeTest).length > 0 &&
      !draft
        .filter((redirect) => redirect.isThemeTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      const themeRedirectGroupKeys = Object.keys(
        groupBy(
          draft.filter((_) => _.isThemeTest),
          (_) => _.group
        )
      );
      if (themeRedirectGroupKeys) {
        draft = addGroupRedirect(
          draft,
          testGroup,
          Number(themeRedirectGroupKeys[0]),
          true,
          false
        );
      }
    }

    // template redirects
    if (
      draft.filter((redirect) => redirect.isTemplateTest).length > 0 &&
      !draft
        .filter((redirect) => redirect.isTemplateTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      draft = addGroupRedirect(draft, testGroup, 1, false, true);
    }
  });
  return draft;
};

export const ensureRedirectGroupOrder = (redirects: RedirectDto[]) => {
  const nonUrlRedirects = redirects.filter(
    (redirect) => redirect.isTemplateTest || redirect.isThemeTest
  );

  const urlRedirectsByGroup = groupBy(
    getSpecificRedirectsUtil(redirects, "url"),
    (redirect) => redirect.group
  );

  const updatedRedirectsByGroup: RedirectDto[] = [];

  Object.values(urlRedirectsByGroup).forEach((redirectGroup, index) => {
    redirectGroup.forEach((redirect) => {
      updatedRedirectsByGroup.push({ ...redirect, group: index + 1 });
    });
  });

  return updatedRedirectsByGroup.concat(nonUrlRedirects);
};

export const ensureUrlRedirectPathFormat = (
  redirects: RedirectDto[],
  domain: string
) => {
  const updatedRedirects: RedirectDto[] = [];

  redirects.forEach((redirect) => {
    let tempRedirect: RedirectDto = redirect;
    if (!redirect.isTemplateTest && !redirect.isThemeTest) {
      if (redirect.filter === "matchesExactly" || redirect.filter === "none") {
        tempRedirect = {
          ...tempRedirect,
          originUrl: ensureFullUrl(tempRedirect, domain, "originUrl"),
        };
      }

      if (
        !redirect.skip &&
        redirect.destinationUrl &&
        redirect.destinationUrl.length > 0 &&
        redirect.redirectType === "fullReplacement"
      ) {
        tempRedirect = {
          ...tempRedirect,
          destinationUrl: ensureFullUrl(tempRedirect, domain),
        };
      }
    }
    updatedRedirects.push(tempRedirect);
  });

  return updatedRedirects;
};
